import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
  // Required for the httpOnly refresh_token cookie to be sent cross-origin
  // (Vercel → Render). Without this, browsers strip cookies from cross-origin requests.
  withCredentials: true,
});

// ── Request interceptor — attach access_token to every request ────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor — silent 401 retry via cookie refresh ────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 429) {
      const retryAfter = error.response.headers?.['retry-after'] || '60';
      return Promise.reject({
        ...error,
        message: `Too many attempts. Please wait ${retryAfter} seconds before trying again.`,
        isRateLimit: true,
      });
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // POST to /auth/refresh — browser automatically sends the httpOnly
        // refresh_token cookie because withCredentials:true is set on this axios instance.
        // The server reads the cookie, issues new tokens, and rotates the cookie.
        const res = await axios.post(
          `${API_BASE}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const { access_token } = res.data;
        localStorage.setItem('access_token', access_token);
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch {
        // Refresh failed — clear all auth state and go to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        localStorage.removeItem('sub_status');
        localStorage.removeItem('user_role');
        localStorage.removeItem('onboarding_done');
        window.location.href = '/#/login';
      }
    }

    return Promise.reject(error);
  }
);

// ── AUTH ──────────────────────────────────────────────────────────
export const authAPI = {
  login:          (email, password) => api.post('/auth/login', { email, password }),
  register:       (data)            => api.post('/auth/register', data),
  me:             ()                => api.get('/auth/me'),
  logout:         ()                => api.post('/auth/logout'),
  refresh:        ()                => api.post('/auth/refresh', {}),
  getCompany:     ()                => api.get('/auth/company'),
  updateCompany:  (data)            => api.patch('/auth/company', data),
  sendOtp:        (email)           => api.post(`/auth/send-otp?email=${encodeURIComponent(email)}`),
  verifyOtp:      (email, otp)      => api.post(`/auth/verify-otp?email=${encodeURIComponent(email)}&otp=${otp}`),
  forgotPassword: (email)           => api.post(`/auth/forgot-password?email=${encodeURIComponent(email)}`),
  changePassword: (data)            => api.post('/auth/change-password', data),
};

// ── TENDERS ───────────────────────────────────────────────────────
export const tendersAPI = {
  list:          (params) => api.get('/tenders/', { params }),
  get:           (id)     => api.get(`/tenders/${id}`),
  save:          (id)     => api.post(`/tenders/${id}/save`),
  saved:         ()       => api.get('/tenders/saved/list'),
  deadlinePulse: ()       => api.get('/tenders/dashboard/deadline-pulse'),
  analytics:     ()       => api.get('/tenders/analytics/summary'),
  matched:       ()       => api.get('/tenders/matched/for-me'),
  filterOptions: ()       => api.get('/tenders/filters/options'),
};

// ── PAYMENTS ──────────────────────────────────────────────────────
export const paymentsAPI = {
  status:          ()                    => api.get('/payments/status'),
  verify:          (reference, plan_id)  => api.post('/payments/verify', { reference, plan_id }),
  support:         (data)                => api.post('/payments/support', data),
  adminActivate:   (company_id, plan_id) => api.post('/payments/admin/activate', { company_id, plan_id }),
  supportMessages: ()                    => api.get('/payments/admin/support-messages'),
  resolveMessage:  (id)                  => api.patch(`/payments/admin/support-messages/${id}/resolve`),
  myTickets:       ()                    => api.get('/payments/support/my-tickets'),
};

// ── SCRAPERS ──────────────────────────────────────────────────────
export const scrapersAPI = {
  stats:   ()    => api.get('/scrapers/stats'),
  sources: ()    => api.get('/scrapers/sources'),
  logs:    ()    => api.get('/scrapers/logs'),
  run:     (key) => api.post(`/scrapers/run/${key}`),
};

// ── ALERTS ────────────────────────────────────────────────────────
export const alertsAPI = {
  sendTest:     (phone_number, message) => api.post('/alerts/whatsapp/test', { phone_number, message }),
  getSettings:  ()                      => api.get('/alerts/settings'),
  saveSettings: (data)                  => api.post('/alerts/settings', data),
  mine:         (unread_only = false)   => api.get('/alerts/mine', { params: { unread_only } }),
  markRead:     (id)                    => api.patch(`/alerts/${id}/read`),
  markAllRead:  ()                      => api.post('/alerts/mark-all-read'),
};

// ── DOCUMENTS ─────────────────────────────────────────────────────
export const documentsAPI = {
  mine: () => api.get('/documents/mine'),
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  remove: (id) => api.delete(`/documents/${id}`),
};

// ── PUBLIC TENDER SUBMISSIONS ─────────────────────────────────────
export const publicSubmissionsAPI = {
  submit: (data) => api.post('/public-submissions', data),
};

// ── AI ────────────────────────────────────────────────────────────
// Only the two endpoints moved server-side 2026-09-29 (compliance-check,
// compare) — win-probability/bid-assistant/market-report/hse-report are
// still called with raw fetch() from their own pages (a separate, lower-
// priority cleanup: see PROTRACKNG_V2_PHASE1_AUDIT.md technical debt #9).
export const aiAPI = {
  complianceCheck: (payload) => api.post('/ai/compliance-check', payload),
  compare:         (payload) => api.post('/ai/compare', payload),
};

// ── ADMIN ─────────────────────────────────────────────────────────
export const adminAPI = {
  dashboard:           ()                 => api.get('/my-admin/dashboard'),
  users:               (params)           => api.get('/my-admin/users', { params }),
  userAction:          (user_id, action)  => api.post(`/my-admin/users/${user_id}/action?action=${action}`),
  subscriptions:       ()                 => api.get('/my-admin/subscriptions'),
  upgradeSubscription: (company_id, plan) => api.post(`/my-admin/subscriptions/${company_id}/upgrade?plan=${plan}`),
  revenue:             ()                 => api.get('/my-admin/revenue'),
  sources:             ()                 => api.get('/my-admin/sources'),
  toggleSource:        (id)               => api.patch(`/my-admin/sources/${id}/toggle`),
  health:              ()                 => api.get('/my-admin/health'),
  broadcast:           (message)          => api.post(`/my-admin/broadcast?message=${encodeURIComponent(message)}`),
  supportMessages:     ()                 => api.get('/my-admin/support-messages'),
  resolveMessage:      (id)               => api.patch(`/my-admin/support-messages/${id}/resolve`),
  pendingDocuments:    ()                 => api.get('/my-admin/documents/pending'),
  verifyDocument:      (id)               => api.patch(`/my-admin/documents/${id}/verify`),
  rejectDocument:      (id, reason)       => api.patch(`/my-admin/documents/${id}/reject`, { reason }),
  downloadDocument:    (id)               => api.get(`/my-admin/documents/${id}/download`),
  generateAlerts:      ()                 => api.post('/alerts/generate'),
  pendingSubmissions:  ()                 => api.get('/my-admin/public-submissions/pending'),
  approveSubmission:   (id)               => api.patch(`/my-admin/public-submissions/${id}/approve`),
  rejectSubmission:    (id, reason)       => api.patch(`/my-admin/public-submissions/${id}/reject`, { reason }),
};

export default api;
